﻿import { appInsights } from "../AppInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { userManager } from "../auth/PrivacyAuthProvider";
import { setupCaptureConsoleError } from "./consoleErrorHandler";
import { decodeClaims } from "../auth/userContextProvider/UserContextProvider.tsx";
import { getJwtToken } from "./api/apiShared.ts";

export function setupErrorHandling() {
    window.addEventListener("unhandledrejection", async function (e) {
        await handleError(e.reason as Error);
    });

    window.addEventListener("error", async (event) => {
        await handleError(event.error, undefined, event);
    });

    setupCaptureConsoleError();
}

export async function handleError(e: Error, componentStack?: string | null, event?: ErrorEvent) {
    if (isResizeObserverError(event) || isBrowserExtensionError(e, event)) {
        event?.stopImmediatePropagation();
        event?.stopPropagation();
        event?.preventDefault();
        return;
    }

    if (e?.message?.includes("No matching state found in storage")) {
        // If login session is reused. This happens a lot, as this page is often bookmarked by the users
        logWarning(e.message);
        // eslint-disable-next-line no-console
        console.log("Login expired. Trying again...");
        try {
            await userManager.signinRedirect();
        } catch (e: any) {
            logError(e.exception as Error, componentStack);
        }
    } else {
        if (e && logAsWarning(e.message)) {
            logWarning(e.message);
        } else {
            logError(e, componentStack);
        }

        if (import.meta.env.DEV) {
            // Show visual notification for developers with component stack without navigating away from current page
            window.dispatchEvent(
                new CustomEvent("dev-error", {
                    detail: {
                        error: e,
                        componentStack: componentStack,
                    },
                })
            );
        } else {
            // Check if we're already on the error page
            if (window.location.pathname.includes("/error")) {
                // eslint-disable-next-line no-console
                console.warn("Error occurred on error page, not redirecting:", e);
            } else {
                window.location.href = "/privacy/error";
            }
        }
    }
}

function logError(error: Error, componentStack?: string | null) {
    const customProperties = { componentStack: componentStack, ...getUserInfo() };
    appInsights.trackException(
        {
            exception: error,
            severityLevel: SeverityLevel.Error,
        },
        customProperties
    );
}

function logWarning(message: string) {
    appInsights.trackTrace({ message: message, severityLevel: SeverityLevel.Warning }, { ...getUserInfo() });
}

function getUserInfo() {
    const jwtToken = getJwtToken();
    if (!jwtToken) {
        return {};
    }
    const userClaims = decodeClaims(jwtToken);
    return {
        UserId: userClaims.UserProfileId,
        UserDomain: userClaims.EmailAddress.split("@")[1],
        CustomerId: userClaims.CustomerId,
        CustomerName: userClaims.CustomerName,
    };
}

function logAsWarning(message: string) {
    const logAsWarningWhiteList: Array<string> = [
        "Object Not Found Matching Id", // Coming from Chromium based web crawlers. Can be ignored: https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
    ];
    return logAsWarningWhiteList.some((x) => message.includes(x));
}

function isBrowserExtensionError(error: Error, event?: ErrorEvent): boolean {
    const message = error?.message || event?.message || "";
    const stack = error?.stack || "";

    // Browser extension patterns
    return (
        message.includes("activeTab") ||
        message.includes("chrome.tabs") ||
        message.includes("browser.tabs") ||
        message.includes("Extension context") ||
        message.includes("chrome.runtime") ||
        stack.includes("webkit-masked-url") ||
        stack.includes("extension://") ||
        stack.includes("safari-extension://") ||
        stack.includes("moz-extension://")
    );
}

function isResizeObserverError(event?: ErrorEvent): boolean {
    // These occur from time to time and can be safely ignored: https://stackoverflow.com/a/64257593
    const message = event?.message || "";

    return message.includes("ResizeObserver loop completed with undelivered notifications") || message.includes("ResizeObserver loop limit exceeded");
}
